export type TokenCounter = (text: string) => number;

/**
 * A tokenizer-free estimate: ASCII at three characters a token (over-counts
 * English by roughly a third against o200k), and every other code point as
 * a token of its own, two outside the BMP (emoji, rare CJK), where o200k
 * spends one or more per character. Still an estimate — callers that face
 * a hard provider limit trim below it.
 */
export const estimateTokens: TokenCounter = (text) => {
  let ascii = 0;
  let other = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x80) {
      ascii += 1;
    } else {
      other += code > 0xff_ff ? 2 : 1;
    }
  }
  return Math.ceil(ascii / 3) + other;
};

const ELLIPSIS = "…";

export function trimToTokens(
  text: string,
  maxTokens: number,
  count: TokenCounter = estimateTokens
): string {
  const trimmed = text.trim();
  if (maxTokens <= 0) {
    return "";
  }
  if (count(trimmed) <= maxTokens) {
    return trimmed;
  }
  let low = 0;
  let high = trimmed.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (count(trimmed.slice(0, mid) + ELLIPSIS) <= maxTokens) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  const cut = trimmed.slice(0, low);
  const lastSpace = cut.lastIndexOf(" ");
  return `${lastSpace > low / 2 ? cut.slice(0, lastSpace) : cut}${ELLIPSIS}`;
}

export function keepNewestWithin<T>(
  items: readonly T[],
  maxTokens: number,
  tokensOf: (item: T) => number,
  maxItems: number = items.length
): T[] {
  const kept: T[] = [];
  let used = 0;
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i] as T;
    const cost = tokensOf(item);
    if (kept.length >= maxItems || used + cost > maxTokens) {
      break;
    }
    kept.unshift(item);
    used += cost;
  }
  return kept;
}
